// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import { HasIsLoading } from "@awc/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

// XNG-Change: add HasIsLoading
type Props = ProductCardSelections & HasProduct & HasIsLoading;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    // productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productInformationStyles?: ProductListProductInformationStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = ({ isLoading, ...props }) => {
    // XNG-Changes: use styledWrapper
    return (
        <StyledWrapper
            {...styles.gridContainer}
            data-test-selector={`productListProductCard${isLoading ? "" : props.product.id}`}
        >
            <StyledWrapper as="figure" {...styles.leftColumnGridItem}>
                {!isLoading && props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    // XNG-changes
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    // XNG-Changes
                    extendedStyles={styles.productListProductImageStyles}
                    {...{ showCustomBadge: true }}
                    {...{ isLoading }}
                />
            </StyledWrapper>

            <StyledWrapper as="section" {...styles.infoGridItem}>
                <StyledWrapper {...styles.infoGridItem}>
                    {isLoading ? (
                        <Skeleton height="122px" />
                    ) : (
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                            // XNG-Changes
                            {...{ extendedStyles: styles.productInformationStyles }}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper as="footer" {...styles.actionsGridItem}>
                <ProductListActions
                    showPrice={props.showPrice}
                    showAddToList={props.showAddToList}
                    // XNG-Changes

                    {...{
                        showCompare: props.showCompare,
                        showAvailability: props.showAvailability,
                        showUnitOfMeasure: true,
                        includeBreakPriceRange: true,
                        isLoading,
                    }}
                />
            </StyledWrapper>
            {/* XNG-Change: add !isLoading */}
            {!isLoading && (
                <StyledWrapper {...styles.searchScoreDataGridItem}>
                    <ProductSearchScoreData />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
